/**
 * MetalUI platform brand — the SINGLE SOURCE OF TRUTH for the global,
 * pre-login platform identity.
 *
 * MetalUI is the platform brand shown before a tenant is resolved:
 *   - the sign-in / sign-up screens
 *   - the pending-approval screen
 *   - the very first paint for a device that has never loaded a tenant
 *   - the PWA manifest / installed-app icon when no tenant is known
 *   - `GET /api/branding` for any tenant that has not saved customisation
 *
 * After login the app rebrands to the resolved tenant's `app_branding`
 * row (see the frontend `BrandingProvider`); these values are only the
 * fallback used when no tenant brand is available.
 *
 * This module is intentionally dependency-free (no React, no Node, no
 * drizzle) so it can be imported by BOTH the React frontend bundle and the
 * Node/esbuild API server, plus any future MetalUI marketing site, without
 * pulling heavy transitive deps into the browser bundle.
 *
 * NOTE on the things that DON'T import this:
 *   - Tailwind arbitrary-value classes (e.g. `text-[#D4AF37]` in the
 *     sign-in page or `clerk-appearance.ts`) MUST stay as literal class
 *     strings — Tailwind's JIT only generates classes it can see as
 *     literals in source, so injecting a JS constant would silently drop
 *     the colour. Those literals are documented to match the values here.
 *   - The `app_branding` Drizzle column defaults are kept as string
 *     literals so `drizzle-kit` (which loads the schema with its own
 *     bundler) never has to resolve a workspace import; they are likewise
 *     documented to match `primaryColor` / `secondaryColor` / `appName`.
 *
 * Brand guide reference (2026 refresh) — the "MetalUI" name plus shield
 * mark, built on a Blue / Gold / Silver metals palette against dark
 * neutral surfaces. `PALETTE` below is the literal named swatch list from
 * the brand sheet; `primaryColor` / `secondaryColor` / `accentColor` are
 * the three that feed the platform's functional theme roles.
 */
/**
 * EverydayBullion legacy gold hex — retained as the platform's named gold
 * color for specific fixed uses: the `--brand-gold` CSS token value, the
 * `FLAG_COLOURS` picker palette, and color-picker placeholder defaults that
 * must supply a literal hex (HTML `<input type="color">` does not accept CSS
 * variables).  All interactive/accent Tailwind classes and inline styles
 * should reference `hsl(var(--primary))` or semantic tokens instead so they
 * respond to per-tenant branding.
 */
export const BRAND_GOLD_HEX = "#F5BB00";

export const METALUI_BRAND = {
  /** Platform application name / wordmark. */
  appName: "MetalUI",
  /** Wordmark tagline shown under the name on the auth screens. */
  tagline: "Precious Metals Platform",
  /**
   * Primary brand colour — Metal Blue (#1E3A8A).
   *
   * CSS token mapping in `artifacts/everyday-bullion/src/index.css`:
   *   - Light mode: `--primary` = 225 64% 33%  → buttons, links, active states
   *   - Light mode: `--ring`    = 46 65% 52%   → focus rings (Metal Gold)
   *   - Dark mode:  `--primary` = 46 65% 52%   → CTA / accent (Metal Gold)
   *   - Dark mode:  `--ring`    = 225 64% 50%  → focus rings (Metal Blue)
   *   - Both modes: `--sidebar-primary` = Metal Gold (active sidebar item)
   *
   * `applyBranding` in `branding-provider.tsx` sets `--primary`/`--ring` to
   * this value for unconfigured tenants, and `BrandingProvider` overrides it
   * with the tenant's saved `app_branding.primary_color` once resolved.
   *
   * Also the PWA `theme_color` (installed icon bar tint on Android/Chrome).
   */
  primaryColor: "#1E3A8A",
  /**
   * Secondary brand colour — Steel Gray (#4B5563).
   *
   * CSS token: `--secondary` in both modes. Drives the secondary panel /
   * charcoal surface colour. `applyBranding` maps it to `--secondary` and
   * `--sidebar-accent` (hover state in the navigation sidebar).
   */
  secondaryColor: "#4B5563",
  /**
   * Accent colour — Metal Gold (#D4AF37).
   *
   * CSS token: sidebar-primary active item (both modes), primary CTA in
   * dark mode, ring/focus accent in light mode. Also used for
   * `.glow-gold`, `.gradient-text-gold`, and the shimmer overlay warmth
   * in `--elevate-*` (dark mode).
   */
  accentColor: "#D4AF37",
  /**
   * Full named palette from the brand guide. All five hex values are the
   * direct sources for the CSS variables in `index.css` — any drift between
   * here and the stylesheet should be considered a bug.
   *
   *   darkSlate  → dark `--background`  (deepest surface)
   *   charcoal   → dark `--card`        (elevated card surface)
   *   metalBlue  → light `--primary`, dark `--secondary`/`--ring`
   *   metalGold  → dark `--primary`, both `--sidebar-primary`, light `--ring`
   *   steelGray  → `--secondary` base, muted foreground tones
   *   metalSilver → chart-2 (platinum/silver precious metal)
   */
  palette: {
    metalBlue: "#1E3A8A",
    metalGold: "#D4AF37",
    metalSilver: "#C0C0C0",
    darkSlate: "#0F1115",
    charcoal: "#1A1D24",
    steelGray: "#4B5563",
  },
  /** Brand typeface — Inter, loaded via Google Fonts in `index.html`. */
  fontFamily: "Inter, system-ui, sans-serif",
  /**
   * Favicon / wordmark logo filenames, served from each artifact's static
   * `public/` dir. Callers prepend the app base path
   * (`import.meta.env.BASE_URL`).
   *
   * Two emblem families ship as cleaned transparent PNGs:
   *   - the full horizontal LOGO lockup (shield + "MetalUI" wordmark), and
   *   - the square SHIELD mark on its own (for tight, square logo slots that
   *     already render the wordmark as separate text).
   * The LOGO lockup comes in colour / black / white so callers can pick the
   * variant that reads against the surface: black on light, white on dark,
   * colour where the surface tolerates the full palette. The SHIELD ships as a
   * single full-colour mark whose blue rim reads cleanly on both light and dark
   * surfaces, so the black/white shield aliases intentionally point at the same
   * colour artwork. `faviconFile` is that colour shield (it reads on both light
   * and dark browser-tab bars).
   */
  faviconFile: "metalui-favicon.png",
  /** Full logo lockup — solid black (default; reads on light surfaces). */
  logoFile: "metalui-logo-black.png",
  /** Full logo lockup — full colour. */
  logoFileColour: "metalui-logo-colour.png",
  /** Full logo lockup — white (reads on dark surfaces). */
  logoFileWhite: "metalui-logo-white.png",
  /** Square shield mark — full colour (reads on light and dark). */
  shieldFile: "metalui-shield-colour.png",
  /** Square shield mark — colour artwork (alias; reads on light surfaces). */
  shieldFileBlack: "metalui-shield-black.png",
  /** Square shield mark — colour artwork (alias; reads on dark surfaces). */
  shieldFileWhite: "metalui-shield-white.png",
  /**
   * The MetalUI platform domain. Each tenant gets a dedicated subdomain
   * (`<slug>.metalui.co.uk`) that auto-resolves their workspace. The
   * runtime value can be overridden via the `METALUI_PLATFORM_DOMAIN`
   * env var.
   */
  platformDomain: "metalui.co.uk",
  /**
   * Platform default outbound email sender. Used by the EmailService as the
   * `from` address when a tenant has NOT set its own `fromEmail` in Email
   * Settings. Only applies to API-based providers (Resend) where a custom
   * `from` is honoured — mailbox-connected providers (Gmail/Outlook/SMTP)
   * always send from the tenant's CONNECTED account and cannot be overridden
   * to an arbitrary address. For this to deliver via Resend the
   * `metalui.co.uk` domain must be a verified sending domain in Resend.
   */
  defaultFromEmail: "no-reply@metalui.co.uk",
} as const;

export type MetalUIBrand = typeof METALUI_BRAND;
